import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  CheckCircle2,
  Download,
  Globe,
  Sun,
  Moon,
  Loader2,
  XCircle,
  PartyPopper,
  RefreshCw,
  ArrowLeft,
  Play,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { t, language, changeLanguage, languages } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  const [status, setStatus] = useState("checking");
  const [content, setContent] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const checkStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    try {
      const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);

      if (data.payment_status === "paid") {
        setStatus("success");
        setAccessToken(data.access_token);

        // Fetch content details
        if (data.access_token) {
          try {
            const contentRes = await axios.get(
              `${API}/content/${data.access_token}`
            );
            setContent(contentRes.data);
          } catch (e) {
            console.error("Failed to fetch content:", e);
          }
        }
      } else if (data.status === "expired") {
        setStatus("expired");
      } else if (data.status === "open") {
        // Still in checkout or processing
        if (pollCount < 15) {
          setPollCount((prev) => prev + 1);
        } else {
          setStatus("pending");
        }
      } else {
        // Continue polling for other statuses
        if (pollCount < 15) {
          setPollCount((prev) => prev + 1);
        } else {
          setStatus("pending");
        }
      }
    } catch (err) {
      console.error("Status check error:", err);
      // Retry a few more times before giving up
      if (pollCount < 8) {
        setPollCount((prev) => prev + 1);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, pollCount]);

  useEffect(() => {
    checkStatus();
  }, []);

  useEffect(() => {
    if (status === "checking" && pollCount > 0 && pollCount < 10) {
      const timer = setTimeout(checkStatus, 2000);
      return () => clearTimeout(timer);
    }
  }, [pollCount, status, checkStatus]);

  const handleDownload = () => {
    if (accessToken && content?.media_file_id) {
      window.open(`${API}/content/${accessToken}/download`, "_blank");
    }
  };

  const retryCheck = () => {
    setPollCount(0);
    setStatus("checking");
    setTimeout(checkStatus, 500);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="payment-success-page">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/80 backdrop-blur-xl z-50">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-black tracking-tight bg-gradient-to-r from-[#0B1F3A] to-[#3A6D8C] bg-clip-text text-transparent dark:from-white dark:to-slate-300">
            Ellalink
          </Link>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-xl">
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? "bg-muted" : ""}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-xl">
              {theme === "light" ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8 sm:py-12 md:py-20">
        <Card className="border-border shadow-xl shadow-[#0B1F3A]/5 dark:shadow-white/5 overflow-hidden">
          <CardContent className="p-5 sm:p-8 md:p-12 text-center">
            {status === "checking" && (
              <div className="animate-in fade-in duration-500">
                <div className="w-24 h-24 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-8">
                  <Loader2 className="h-12 w-12 text-primary animate-spin" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mb-3">{t("payment.verifyingPayment")}</h1>
                <p className="text-muted-foreground">
                  {t("payment.pleaseWait")}
                </p>
                <div className="flex justify-center gap-1 mt-6">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="w-2 h-2 rounded-full bg-primary animate-bounce"
                      style={{ animationDelay: `${i * 150}ms` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {status === "success" && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="w-24 h-24 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-8 relative">
                  <CheckCircle2 className="h-14 w-14 text-emerald-500" />
                  <div className="absolute -top-2 -right-2">
                    <PartyPopper className="h-8 w-8 text-amber-500" />
                  </div>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-3">{t("payment.success")}</h1>
                <p className="text-muted-foreground text-lg mb-8">
                  {content?.success_message || t("payment.thankYou")}
                </p>

                {/* Unlocked media preview */}
                {content?.media_file_id && (
                  <div className="rounded-2xl overflow-hidden mb-8 border border-border shadow-lg" data-testid="unlocked-media">
                    {content.media_content_type?.startsWith("image/") ? (
                      <img
                        src={`${API}/files/${content.media_file_id}?auth=${content.access_token}`}
                        alt={content.title || "Purchased content"}
                        className="w-full max-h-[500px] object-contain bg-muted"
                        data-testid="unlocked-image"
                      />
                    ) : content.media_content_type?.startsWith("video/") ? (
                      <video
                        src={`${API}/files/${content.media_file_id}?auth=${content.access_token}`}
                        controls
                        className="w-full max-h-[500px] bg-black"
                        data-testid="unlocked-video"
                      >
                        {t("payment.videoNotSupported")}
                      </video>
                    ) : null}
                  </div>
                )}

                {content?.title && (
                  <div className="bg-muted/50 rounded-2xl p-6 mb-8 text-left">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t("payment.yourPurchase")}</p>
                    <h3 className="font-bold text-xl mb-2">{content.title}</h3>
                    {content.description && (
                      <p className="text-sm text-muted-foreground">
                        {content.description}
                      </p>
                    )}
                  </div>
                )}

                {content?.media_file_id && (
                  <Button
                    className="w-full h-14 text-lg font-semibold rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 bg-emerald-500 hover:bg-emerald-600 transition-all duration-300 hover:-translate-y-0.5"
                    onClick={handleDownload}
                    data-testid="download-btn"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    {t("payment.download")}
                  </Button>
                )}

                <div className="mt-8 pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t("payment.saveThisPage")}
                  </p>
                  <code className="text-xs bg-muted px-3 py-1.5 rounded-lg">
                    {accessToken?.slice(0, 20)}...
                  </code>
                </div>
              </div>
            )}

            {status === "pending" && (
              <div className="animate-in fade-in duration-500">
                <div className="w-24 h-24 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-8">
                  <Loader2 className="h-12 w-12 text-amber-500 animate-spin" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mb-3">{t("payment.paymentProcessing")}</h1>
                <p className="text-muted-foreground mb-8">
                  {t("payment.paymentStillProcessing")}
                </p>
                <Button
                  variant="outline"
                  className="h-12 px-8 rounded-xl"
                  onClick={retryCheck}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t("payment.checkAgain")}
                </Button>
              </div>
            )}

            {(status === "error" || status === "expired") && (
              <div className="animate-in fade-in duration-500">
                <div className="w-24 h-24 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-8">
                  <XCircle className="h-14 w-14 text-destructive" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mb-3">
                  {status === "expired" ? t("payment.sessionExpired") : t("payment.somethingWrong")}
                </h1>
                <p className="text-muted-foreground mb-8">
                  {status === "expired"
                    ? t("payment.sessionExpiredDesc")
                    : t("payment.somethingWrongDesc")}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="outline"
                    className="h-12 px-6 rounded-xl"
                    onClick={() => window.history.back()}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {t("payment.goBack")}
                  </Button>
                  <Button
                    className="h-12 px-6 rounded-xl"
                    onClick={retryCheck}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t("payment.tryAgain")}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
